import { int } from '../lib/format';
import { C, EASE_SPRING, ink, zoneStyle } from '../lib/theme';
import type { CrmClientRow } from '../lib/types';
import { IconAlert, IconArrowRight, IconCheck, IconCircleDashed, IconClock, IconExternal } from './icons';

type Adimplencia = 'adimplente' | 'pendente' | 'inadimplente' | 'sem_fatura';

/**
 * `status_atual` já vem pronto do backend: é o status da fatura mais recente
 * do cliente DENTRO do período filtrado — nunca "teve alguma falha alguma
 * vez" (correção de rumo real: uma falha isolada e antiga não torna o
 * cliente inadimplente hoje se ele já pagou depois). `faturas_falhas` só
 * entra como contador informativo no rodapé do card, não decide o badge.
 */
function adimplenciaOf(c: CrmClientRow): Adimplencia {
  if (c.status_atual === 'falhou') return 'inadimplente';
  if (c.status_atual === 'pendente') return 'pendente';
  if (c.status_atual === 'pago') return 'adimplente';
  return 'sem_fatura';
}

const ADIMPLENCIA_TONE: Record<Adimplencia, { bg: string; border: string; color: string; icon: React.ReactNode; label: string; wash: string }> = {
  adimplente: {
    bg: 'rgba(46,204,113,0.16)', border: 'rgba(46,204,113,0.42)', color: C.green, icon: <IconCheck size={13} />, label: 'Adimplente',
    wash: 'linear-gradient(100deg, rgba(46,204,113,0.09), rgba(253,250,249,0.03) 60%)',
  },
  pendente: {
    bg: 'rgba(245,165,36,0.16)', border: 'rgba(245,165,36,0.44)', color: C.amber, icon: <IconClock size={13} />, label: 'Pendente',
    wash: 'linear-gradient(100deg, rgba(245,165,36,0.1), rgba(253,250,249,0.03) 60%)',
  },
  inadimplente: {
    bg: 'rgba(242,85,90,0.16)', border: 'rgba(242,85,90,0.44)', color: C.churn, icon: <IconAlert size={13} />, label: 'Inadimplente',
    wash: 'linear-gradient(100deg, rgba(242,85,90,0.11), rgba(253,250,249,0.03) 60%)',
  },
  sem_fatura: {
    bg: ink(0.07), border: ink(0.16), color: ink(0.5), icon: <IconCircleDashed size={13} />, label: 'Sem fatura no período',
    wash: ink(0.035),
  },
};

/**
 * Bloco de cliente — original, não é uma variação do `EventCard` do módulo CS
 * (que é linha densa de auditoria; aqui a pergunta é outra: "esse cliente
 * está em dia, e com o quê ele está?"). Card com respiro de verdade entre um
 * e outro (a lista usa gap generoso, não zebra colada) — o destaque principal
 * é a adimplência, não o MRR.
 */
export function ClientCard({ client, onOpen }: {
  client: CrmClientRow;
  onOpen: () => void;
}) {
  const c = client;
  const adimplencia = adimplenciaOf(c);
  const tone = ADIMPLENCIA_TONE[adimplencia];
  const displayName = c.nome_comercial || c.nome || c.master_id;
  const assinaturas = int(c.assinaturas_ativas);
  const faturas = int(c.faturas_total);

  return (
    <div
      className="ko-client-card"
      style={{
        display: 'flex', flexDirection: 'column', gap: 13, padding: '18px 20px',
        borderRadius: 18, background: tone.wash, border: `1px solid ${tone.border}55`,
        borderLeft: `4px solid ${tone.color}`, cursor: 'pointer',
        boxShadow: `-1px 0 18px -6px ${tone.color}40`,
        transition: `background 0.18s ease, border-color 0.18s ease, transform 0.22s ${EASE_SPRING}`,
      }}
      onClick={onOpen}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, flex: 'none',
          fontSize: 12.5, fontWeight: 800, letterSpacing: '0.02em',
          padding: '6px 14px', borderRadius: 99,
          background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color,
        }}>{tone.icon}{tone.label}</span>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 17.5, fontWeight: 800, letterSpacing: '-0.014em', color: C.fg,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{displayName}</div>
          <div style={{
            fontSize: 12, color: ink(0.44), marginTop: 2, fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{c.master_id}</div>
        </div>

        {c.status_crm && (
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase',
            padding: '4px 10px', borderRadius: 99, flex: 'none', background: ink(0.08), color: ink(0.62),
          }}>{c.status_crm}</span>
        )}

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="ko-client-open"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, flex: 'none',
            border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 780,
            padding: '9px 16px', borderRadius: 11,
            background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`, color: '#fff',
            boxShadow: '0 6px 18px -2px rgba(232,85,48,0.55)',
            transition: `transform 0.25s ${EASE_SPRING}`,
          }}
        >
          Abrir ficha<IconArrowRight size={13} />
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        {(c.zonas ?? []).map((z) => {
          const zs = zoneStyle(z);
          const stripeId = c.zona_stripe_ids?.[z];
          const stripeUrl = stripeId ? `https://dashboard.stripe.com/subscriptions/${stripeId}` : null;
          return (
            <button
              key={z} type="button" className="ko-zone"
              title={stripeUrl ? `Abrir assinatura de ${z} no Stripe` : `${z} · sem stripe_subscription_id disponível`}
              disabled={!stripeUrl}
              onClick={(e) => { e.stopPropagation(); if (stripeUrl) window.open(stripeUrl, '_blank', 'noopener,noreferrer'); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12.5, fontWeight: 750, letterSpacing: '-0.005em',
                padding: '6px 14px 6px 14px', borderRadius: 99, background: zs.bg, color: zs.color,
                border: `1px solid ${zs.color}55`, cursor: stripeUrl ? 'pointer' : 'default', font: 'inherit',
                boxShadow: `0 0 14px -5px ${zs.color}`, opacity: stripeUrl ? 1 : 0.6,
              }}
            >{z}{stripeUrl && <IconExternal size={11} />}</button>
          );
        })}
        {/* Produto só aparece quando diz mais do que a zona já disse — em
            planos de produto único, categoria e zona costumam ser o mesmo
            nome (ex: "POWERBOT" e "PowerBot"), e repetir o chip é ruído. */}
        {(c.produtos ?? [])
          .filter((p) => !(c.zonas ?? []).some((z) => z.toUpperCase() === p.toUpperCase()))
          .map((p) => (
            <span key={p} style={{
              fontSize: 11.5, fontWeight: 650, padding: '4px 12px', borderRadius: 99,
              background: ink(0.06), border: `1px solid ${ink(0.12)}`, color: ink(0.7),
            }}>{p}</span>
          ))}
        {c.tags?.map((t) => (
          <span key={t} style={{
            fontSize: 11.5, fontWeight: 700, padding: '4px 12px', borderRadius: 99,
            background: 'rgba(255,107,71,0.1)', border: `1px solid rgba(255,144,112,0.3)`, color: C.accent,
          }}>#{t}</span>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 11.5, color: ink(0.38) }}>
        <span>{assinaturas} assinatura{assinaturas === 1 ? '' : 's'} ativa{assinaturas === 1 ? '' : 's'}</span>
        {faturas > 0 && <span>{faturas} fatura{faturas === 1 ? '' : 's'} no histórico</span>}
      </div>
    </div>
  );
}
